import '@assets/styles/tiptap-editor.css';

import { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import { brandColors } from '@src/assets/colors/brandColors';
import IconChatEditorSend from '@src/assets/images/common/icon/editor/icon_chat_editor_send.svg';
import SvgIconEmail from '@src/assets/images/common/icon/editor/SvgIconEmail';
import SvgIconEmoji from '@src/assets/images/common/icon/editor/SvgIconEmoji';
import SvgIconPlus from '@src/assets/images/common/icon/editor/SvgIconPlus';
import EmojiBottomMenuSp from '@src/features/shared/community/components/chat/EmojiBottomMenuSp';
import EmojiDropdownMenuPc from '@src/features/shared/community/components/chat/EmojiDropdownMenuPc';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { useDropdownPosition } from '@src/hooks/useDropdownPosition';
import useIsMobile from '@src/hooks/useIsMobile';
import { EditorFeatures } from '@src/ui/Editor';
import Checkbox from '@src/ui/elements/form/Checkbox';
import { EmojiClickData } from 'emoji-picker-react';
import React, { useCallback, useRef, useState } from 'react';

export interface EditorChatFooterProps {
  features: EditorFeatures;
  onSend?: () => void;
  onSelectFile?: (fileList: FileList) => void;
  onSelectEmoji?: (emojiObject: EmojiClickData) => void;
  onTriggerMention?: () => void;
  isMentionListActive?: boolean;
  disabled?: boolean;
  isAnonymous?: boolean;
  onChangeIsAnonymous?: (isAnonymous: boolean) => void;
  isEditing?: boolean;
  allowFileAttachment?: boolean;
  allowAnonymousPost?: boolean;
  onCancelEdit?: () => void;
  channelCode?: string;
  channelName?: string;
  channelMembers?: ChannelMember[];
  channelVisibility?: number;
  editedChatId?: number;
  currentUserId?: number;
  currentUserType?: string;
  isLoggedIn?: boolean;
}

/**
 * EditorChatFooter
 * Community chat specific footer with file attachment, emoji picker, mention, and send functionality
 */
export const EditorChatFooter: React.FC<EditorChatFooterProps> = ({
  features: _features, // Keep for interface consistency
  onSend = () => {},
  onSelectFile = () => {},
  onSelectEmoji = () => {},
  onTriggerMention = () => {},
  isMentionListActive = false,
  disabled = false,
  isAnonymous = false,
  onChangeIsAnonymous = () => {},
  isEditing = false,
  allowFileAttachment = false,
  allowAnonymousPost = false,
  onCancelEdit = () => {},
}) => {
  // Explicitly mark features as intentionally unused for now
  void _features;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const emojiMenuRef = useRef<HTMLDivElement>(null);
  const [isEmojiMenuOpen, setEmojiMenuOpen] = useState(false);

  // Platform detection and keyboard shortcuts
  const isMac = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac');
  const shortcutKeyLabel = isMac ? '⌘ + Enter' : 'Ctrl + Enter';
  const isMobile = useIsMobile();

  // Dropdown positioning
  const {
    position: emojiPosition,
    below: menuPositionBelow,
    calculatePosition,
  } = useDropdownPosition(460, 5, 'left');

  const toggleEmoji = useCallback(() => {
    calculatePosition(emojiButtonRef);
    setEmojiMenuOpen((value) => !value);
  }, [calculatePosition]);

  const handleCloseEmojiMenu = useCallback(() => {
    setEmojiMenuOpen(false);
  }, []);

  const handleEmojiSelect = useCallback(
    (emoji: EmojiClickData) => {
      onSelectEmoji(emoji);
      setEmojiMenuOpen(false);
    },
    [onSelectEmoji],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        onSelectFile(event.target.files);
        event.target.value = '';
      }
    },
    [onSelectFile],
  );

  // Click outside handler for emoji menu
  useClickOutside([emojiButtonRef, emojiMenuRef], handleCloseEmojiMenu, isEmojiMenuOpen);

  const activeColor = brandColors['500'] || '#2b5cdd';
  const inactiveColor = 'currentColor';

  return (
    <div className='chatFooter'>
      <div className='flex items-center gap-2'>
        {/* File attachment */}
        {allowFileAttachment && (
          <>
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              title='ファイルを追加'
              className='toolbar-button'
              style={{ padding: '0px' }}
            >
              <SvgIconPlus color={inactiveColor} />
            </button>
            <input
              ref={fileInputRef}
              type='file'
              className='hidden'
              multiple
              onChange={handleFileChange}
            />
          </>
        )}

        {/* Emoji picker */}
        <button
          type='button'
          ref={emojiButtonRef}
          onClick={toggleEmoji}
          title='絵文字'
          className='toolbar-button'
          style={{ padding: '0px' }}
        >
          <SvgIconEmoji color={isEmojiMenuOpen ? activeColor : inactiveColor} />
        </button>

        {/* Mention trigger */}
        <button
          type='button'
          title='メンション'
          onClick={onTriggerMention}
          className={`toolbar-button ${isMentionListActive ? 'bg-blue-100 text-blue-600' : ''}`}
          style={{ padding: '0px' }}
        >
          <SvgIconEmail color={isMentionListActive ? activeColor : inactiveColor} />
        </button>
      </div>

      <div className='flex items-center gap-3'>
        {allowAnonymousPost && (
          <div className='flex items-center gap-[10px]'>
            <Checkbox
              id='email-notification'
              checked={isAnonymous}
              onChange={() => onChangeIsAnonymous(!isAnonymous)}
            />
            <label
              htmlFor='email-notification'
              className='cursor-pointer text-sm'
            >
              匿名
            </label>
          </div>
        )}

        {/* Cancel edit button */}
        {isEditing && (
          <button
            onClick={onCancelEdit}
            className='flex h-6 items-center justify-center rounded border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50'
            type='button'
          >
            キャンセル
          </button>
        )}

        {/* Send button */}
        <button
          type='button'
          onClick={onSend}
          disabled={disabled}
          className={`flex h-6 w-10 items-center justify-center rounded ${
            disabled ? 'send-button cursor-not-allowed' : 'send-button--enabled cursor-pointer'
          }`}
          title={`送信（${shortcutKeyLabel}）`}
        >
          <img
            src={IconChatEditorSend}
            alt='送信'
            className='mx-auto'
          />
        </button>
      </div>

      {/* Emoji picker menu */}
      {isEmojiMenuOpen && (
        <>
          {!isMobile && (
            <div ref={emojiMenuRef}>
              <EmojiDropdownMenuPc
                onEmojiClick={handleEmojiSelect}
                position={emojiPosition}
                menuPositionBelow={menuPositionBelow}
                horizontalAlign='left'
              />
            </div>
          )}
          {isMobile && (
            <EmojiBottomMenuSp
              handleCloseClick={handleCloseEmojiMenu}
              handleEmojiClick={handleEmojiSelect}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EditorChatFooter;
